import { useState } from 'react';
import { Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import type { Blueprint, ToolInfo, WorkflowNode } from './agentBuilderApi';

const KIND_TEXT: Record<string, { title: string; what: string }> = {
  start: { title: 'Start', what: 'What starts a run, and the values the run receives.' },
  action: { title: 'Tool call', what: 'Calls an external action through MCP. The agent fills in the inputs from what it knows so far.' },
  ai: { title: 'AI step', what: 'The agent does this itself with its own intelligence (no tool): for example writing the reply.' },
  decision: { title: 'Decision', what: 'A yes/no check on what earlier steps returned. The agent decides it and follows the Yes or No branch.' },
  handoff: { title: 'Hand to a person', what: 'Hands the run to a person and ends it.' },
  end: { title: 'End', what: 'How the run finishes: the outcome it reports.' },
};

// The actions this journey can use: every tool the MCP server offers, the blueprint's own first.
// Collapsed by default so the chat keeps the room; the builder never uses anything not listed here.
export function ToolsPanel({ blueprint, tools, alwaysOpen = false }: { blueprint: Blueprint | null; tools: ToolInfo[]; alwaysOpen?: boolean }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const open = alwaysOpen || expanded;
  const setOpen = setExpanded;
  const base = new Set(blueprint?.tools ?? []);
  const ordered = [...tools.filter((x) => base.has(x.name)), ...tools.filter((x) => !base.has(x.name))];
  return (
    <div className="border-b border-default-200 bg-content1" data-testid="ab-tools-panel">
      <button type="button" className="flex w-full items-center justify-between px-3 py-2 text-sm" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="font-semibold">
          {t('Available actions')} <span className="font-normal text-default-500">({tools.length})</span>
        </span>
        <Icon icon={open ? 'lucide:chevron-up' : 'lucide:chevron-down'} width={16} />
      </button>
      {open && (
        <ul className={`${alwaysOpen ? '' : 'max-h-60 overflow-y-auto'} px-3 pb-2 text-xs`}>
          {ordered.map((tool) => (
            <li key={tool.name} className="border-t border-default-100 py-1.5">
              <div className="flex items-center gap-1">
                <span className="font-semibold">{tool.display_name || tool.name}</span>
                <span className="text-default-400">{tool.name}</span>
                {base.has(tool.name) && (
                  <Chip size="sm" variant="flat" color="primary" className="h-4 text-[10px]">
                    {blueprint?.title}
                  </Chip>
                )}
                {tool.server && (
                  <Chip size="sm" variant="flat" className="ml-auto h-4 text-[10px]">
                    {tool.server} MCP
                  </Chip>
                )}
              </div>
              <div className="text-default-600">{tool.business_description}</div>
            </li>
          ))}
          {blueprint && (
            <li className="border-t border-default-100 py-1.5 text-default-500">
              {t('Outcomes')}: {Object.keys(blueprint.outcomes).join(', ')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// What a node does, when it is clicked on the canvas.
export function NodeDetails({
  node,
  step,
  tool,
  issues,
  onClose,
}: {
  node: WorkflowNode;
  step: number | undefined;
  tool: ToolInfo | undefined;
  issues: string[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const kind = KIND_TEXT[node.type] ?? KIND_TEXT.action;
  const d = node.data;
  return (
    <aside
      className="absolute right-3 top-3 z-10 flex max-h-[calc(100%-1.5rem)] w-80 flex-col gap-2 overflow-y-auto rounded-lg border border-default-200 bg-content1 p-3 text-sm shadow-lg"
      data-testid="ab-node-details"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-default-500">
            {step !== undefined && `${t('Step')} ${step} · `}
            {t(kind.title)}
          </div>
          <div className="font-semibold">{d.label || t(kind.title)}</div>
        </div>
        <CtyButton isIconOnly size="sm" variant="light" onPress={onClose} title={t('Close')}>
          <Icon icon="lucide:x" width={16} />
        </CtyButton>
      </div>
      <p className="text-xs text-default-500">{t(kind.what)}</p>
      {issues.map((m) => (
        <p key={m} className="text-xs text-danger">
          {m}
        </p>
      ))}
      {node.type === 'start' && (
        <Field label={t('Trigger')}>
          {d.trigger} {!!d.inputs?.length && `· ${t('inputs')}: ${d.inputs.join(', ')}`}
        </Field>
      )}
      {(node.type === 'action' || node.type === 'handoff') && (
        <>
          <Field label={t('Tool')}>
            <span className="font-mono">{d.tool}</span>
            {tool?.server && <span className="text-default-500"> · {t('{{server}} MCP server', { server: tool.server })}</span>}
          </Field>
          {tool?.business_description && <Field label={t('What it does')}>{tool.business_description}</Field>}
          {!!tool?.inputs?.length && (
            <Field label={t('Inputs')}>
              <ul className="list-disc pl-4">
                {tool.inputs.map((i) => (
                  <li key={i.name}>
                    <span className="font-mono">{i.name}</span>
                    {!i.required && <span className="text-default-500"> ({t('optional')})</span>}
                    {i.description && <span className="text-default-500"> — {i.description}</span>}
                  </li>
                ))}
              </ul>
            </Field>
          )}
        </>
      )}
      {node.type === 'decision' && <Field label={t('Checks')}>{d.condition}</Field>}
      {node.type === 'handoff' && <Field label={t('Reason')}>{d.reason}</Field>}
      {node.type === 'end' && <Field label={t('Outcome')}>{d.outcome}</Field>}
      {d.note && <Field label={node.type === 'ai' ? t('What the agent does') : t('How')}>{d.note}</Field>}
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-default-600">{label}</div>
      <div className="whitespace-pre-wrap text-xs">{children}</div>
    </div>
  );
}
